import React, { useState, useEffect } from 'react';
import { upgradeApprovalAPI } from '../../services/api';
import { useAuthStore, useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './UpgradeApprovals.css';

const UpgradeApprovals = () => {
    const admin = useAuthStore((state) => state.admin);
    const { selectedBranch } = useBranchStore();
    const canDecide = ['superadmin', 'tenant_admin'].includes(admin?.role);
    const [approvals, setApprovals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');

    useEffect(() => { fetchAll(); }, [filter, selectedBranch]);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const res = await upgradeApprovalAPI.getAll({
                status: filter === 'all' ? undefined : filter,
                branchId: selectedBranch ? selectedBranch._id : undefined,
            });
            setApprovals(res.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch upgrade approvals');
        } finally {
            setLoading(false);
        }
    };

    const handleDecide = async (approval, decision) => {
        const note = decision === 'rejected' ? window.prompt('Reason for rejection (optional):') || '' : '';
        try {
            await upgradeApprovalAPI.decide(approval._id, decision, note);
            toast.success(`Upgrade ${decision}`);
            fetchAll();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to record decision');
        }
    };

    const statusColor = (s) => ({ pending: '#3b82f6', approved: '#16a34a', rejected: '#dc2626' }[s] || '#6b7280');

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern"><i className="fas fa-user-check"></i> Upgrade Approvals</h1>
                        <p className="page-subtitle-modern">Branch admin requests to upgrade a guest to a pricier plan — needs tenant admin or superadmin sign-off before it's applied</p>
                    </div>
                    <div className="header-actions">
                        <select value={filter} onChange={e => setFilter(e.target.value)} className="approvals-filter">
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="all">All</option>
                        </select>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading approvals...</p></div>
            ) : approvals.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-user-check"></i></div>
                    <h3>No {filter !== 'all' ? filter : ''} Approvals</h3>
                    <p>Upgrade requests from branch admins to a pricier plan will show up here.</p>
                </div>
            ) : (
                <div className="table-wrapper-modern">
                    <table className="table-modern">
                        <thead>
                            <tr><th>Guest</th><th>Old Plan</th><th>New Plan</th><th>Requested By</th><th>Status</th>{canDecide && <th>Action</th>}</tr>
                        </thead>
                        <tbody>
                            {approvals.map(a => (
                                <tr key={a._id}>
                                    <td>{a.userId?.name || 'Guest'} <span style={{ color: '#9ca3af' }}>{a.userId?.mobile}</span></td>
                                    <td>{a.oldPlanId?.name || '—'} {a.oldPlanId?.pricing ? `(₹${a.oldPlanId.pricing.amount})` : ''}</td>
                                    <td>{a.newPlanId?.name} (₹{a.newPlanId?.pricing?.amount})</td>
                                    <td>{a.requestedBy?.name || a.requestedBy?.email}</td>
                                    <td><span className="status-badge" style={{ background: `${statusColor(a.status)}15`, color: statusColor(a.status) }}>{a.status}</span></td>
                                    {canDecide && (
                                        <td>
                                            {a.status === 'pending' ? (
                                                <div style={{ display: 'flex', gap: '0.4rem' }}>
                                                    <button className="btn-primary-modern btn-sm" onClick={() => handleDecide(a, 'approved')}><i className="fas fa-check"></i> Approve</button>
                                                    <button className="btn-danger-modern btn-sm" onClick={() => handleDecide(a, 'rejected')}><i className="fas fa-times"></i> Reject</button>
                                                </div>
                                            ) : '—'}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default UpgradeApprovals;
